import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { useCallback, useEffect, useMemo } from 'react';
import { walletRepository } from '../repositories/WalletRepository';
import { setInfos, setNWCConnectionString, setBalance, fetchTransactions, refreshLastTransactions, resetTransactions, addTransaction, markTransactionAsAnnounced, markTransactionAsRead } from '../store/slices/walletSlice';
import { apiResponseToTransaction, Transaction } from '../models/Transactions';
import { websocketService } from '../utils/WebSocketService';
import { Credentials } from '../models/Credentials';

export function useWallet() {
  const dispatch = useDispatch<AppDispatch>();
  const wallet = useSelector((state: RootState) => state.wallet);

  const refresh = useCallback(async () => {
    walletRepository.getWallet()
      .then((wallet) => {
        dispatch(setInfos(wallet));
      });
  }, [dispatch]);

  const regenerateNWCConnectionString = useCallback(async () => {
    walletRepository.regenerateSecretNWC()
      .then((nwcConnectionString) => {
        dispatch(setNWCConnectionString(nwcConnectionString));
      });
  }, [dispatch]);

  const loadTransactions = useCallback((perPage: number = 40) => {
    if(wallet.hasMore && !wallet.isLoading) {
      dispatch(fetchTransactions({ page: wallet.currentPage, perPage }));
    }
  }, [dispatch, wallet.currentPage, wallet.hasMore, wallet.isLoading]);

  const reloadLastTransactions = useCallback(async () => {
    dispatch(refreshLastTransactions());
  }, [dispatch, wallet.transactions]);

  const markTransactionAsReadHook = useCallback((transactionHash: string) => {
    walletRepository.markTransactionAsRead(transactionHash);
    dispatch(markTransactionAsRead(transactionHash));
  }, [dispatch]);

  const markTransactionAsAnnouncedHook = useCallback((transactionHash: string) => {
    walletRepository.markTransactionAsAnnounced(transactionHash);
    dispatch(markTransactionAsAnnounced(transactionHash));
  }, [dispatch]);

  const getTransaction = useCallback(async (paymentHash: string): Promise<Transaction | null> => {
    // If we already have the transaction and not loading, return it immediately
    const existingTransaction = wallet.transactions.find(t => t.paymentHash === paymentHash);
    if (existingTransaction) {
      return existingTransaction;
    }

    const transaction = await walletRepository.getTransaction(paymentHash);
    if(transaction) {
      dispatch(addTransaction(transaction));
    }

    return transaction;
  }, [wallet.transactions, dispatch]);

  const refreshTransaction = useCallback(async (paymentHash: string): Promise<Transaction | null> => {
    const transaction = await walletRepository.getTransaction(paymentHash);
    if(transaction) {
      dispatch(addTransaction(transaction));
    }

    return transaction;
  }, [wallet.transactions, dispatch]);

  const nbTxUnread = useMemo((): number => {
    return wallet.transactions.reduce((count, transaction) => 
      transaction.read === false ? count + 1 : count, 0
    );
  }, [wallet.transactions]);

  const handleVisibilityChange = async () => {
    if (document.hidden) {
      //console.log('Tab hidden, reducing WebSocket activity');
    } else {
      //console.log('Tab visible, resuming normal WebSocket activity');
      
      websocketService.reconnect();

      try {
        await refresh();
        await reloadLastTransactions();
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    }
  };

  const initWallet = useCallback(async (credentials: Credentials) => {
    await refresh();
    loadTransactions();

    const socket = websocketService.connect(credentials.accessToken);

    socket.on('wallet.receive', ({newBalance, payment}: {newBalance: number, payment: Transaction}) => {
      dispatch(setBalance(newBalance));
      dispatch(addTransaction(apiResponseToTransaction(payment))); 
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      websocketService.disconnect();
    };
  }, [setBalance, addTransaction]);

  const unsetWallet = useCallback(() => {
    websocketService.disconnect();
    dispatch(resetTransactions());
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch]);

  return {
    balance: wallet.balance,
    lnurl: wallet.lnurl,
    lockedFunds: wallet.lockedFunds,
    transactions: wallet.transactions,
    isLoading: wallet.isLoading,
    error: wallet.error,
    hasMore: wallet.hasMore,
    nwcConnectionString: wallet.nwcConnectionString,
    refresh,
    regenerateNWCConnectionString,
    loadTransactions,
    reloadLastTransactions,
    getTransaction,
    refreshTransaction,
    nbTxUnread,
    resetTransactions: () => dispatch(resetTransactions()),
    setBalance: useCallback((balance: number) => 
      dispatch(setBalance(balance)), [dispatch]),
    addTransaction: useCallback((transaction: Transaction) => 
      dispatch(addTransaction(transaction)), [dispatch]),
    markTransactionAsRead: markTransactionAsReadHook,
    markTransactionAsAnnounced: markTransactionAsAnnouncedHook,
    initWallet: initWallet,
    unsetWallet: unsetWallet
  };
} 
